import { isNil } from 'lodash';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { ControlsState } from '@shared/components/filter/types';
import { useCountWorkflowEntities } from '@shared/scenes/dashboard/hooks/useCountWorkflowEntities';
import { API } from '@shared/types/api';
import { useChannel, useIsDesktop } from '@shared/util/hooks';
import { BulkUpdateStateProvider } from './BulkUpdateContext';
import { mapCollectionCriteriaToState } from './mapCollectionCriteriaToState';
import { mapStateToCollectionCriteria } from './mapStateToCollectionCriteria';

interface BulkUpdateProviderProps {
  children: ReactNode;
  viewTemplate: API.ViewTemplateFragment;
  workflowTemplateId: string;
  channelId: number;
}

export function BulkUpdateProvider({
  children,
  viewTemplate,
  workflowTemplateId,
  channelId,
}: BulkUpdateProviderProps) {
  const channel = useChannel({ channelId });
  const [showBulkFilterUpdate, setShowBulkFilterUpdate] = useState(false);
  const [showBulkFilterUpdateForm, setShowBulkFilterUpdateForm] = useState(false);
  const [controlsState, setControlsState] = useState<ControlsState>(
    mapCollectionCriteriaToState(null, [], [], []),
  );
  const isDesktop = useIsDesktop();
  const { filterByComponents } = mapStateToCollectionCriteria(controlsState);
  const { search } = controlsState;

  const input = useMemo(() => {
    return {
      viewTemplateId: viewTemplate.id,
      workflowTemplateId,
      filterByComponents,
      omniSearchQuery: search,
    };
  }, [viewTemplate, filterByComponents, workflowTemplateId, search]);

  const { totalResults: totalCardCount, loading: isLoading } = useCountWorkflowEntities({
    input,
    skip: isNil(viewTemplate) || !showBulkFilterUpdate,
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    setShowBulkFilterUpdate(false);
  }, [viewTemplate?.id]);

  return (
    <BulkUpdateStateProvider
      value={{
        showBulkFilterUpdate: isDesktop && showBulkFilterUpdate,
        setShowBulkFilterUpdate,
        showBulkFilterUpdateForm: isDesktop && showBulkFilterUpdateForm,
        setShowBulkFilterUpdateForm,
        totalCardCount,
        isLoading,
        controlsState: controlsState ?? null,
        setControlsState,
        workspaceName: channel?.name ?? '',
      }}
    >
      {children}
    </BulkUpdateStateProvider>
  );
}
